import { Upload as UploadIcon, FileImage, Plus, X } from 'lucide-react';
import type { PageItem } from '@/hooks/useWritingPages';

interface WritingPagesInputProps {
  label: string;
  hint: string;
  pages: PageItem[];
  maxPages: number;
  error: string | null;
  onAdd: () => void;
  onRemove: (id: string) => void;
}

// Drop zone + page thumbnails for writing uploads. Shared by /upload and /try.
export default function WritingPagesInput({ label, hint, pages, maxPages, error, onAdd, onRemove }: WritingPagesInputProps) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium leading-none">
          {label}
        </label>
        <span className="text-xs text-muted-foreground">{pages.length}/{maxPages} pages</span>
      </div>

      {pages.length === 0 ? (
        <button
          type="button"
          onClick={onAdd}
          className="w-full border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer hover:bg-muted/40 transition-colors"
        >
          <div className="flex flex-col items-center gap-2 text-muted-foreground">
            <UploadIcon className="h-8 w-8" />
            <span className="text-sm font-medium">Upload writing</span>
            <span className="text-xs">{hint}</span>
          </div>
        </button>
      ) : (
        /* Thumbnail grid */
        <div className="flex flex-wrap gap-2">
          {pages.map((page, idx) => (
            <div
              key={page.id}
              className="relative w-24 h-24 rounded-md border bg-muted overflow-hidden flex items-center justify-center"
            >
              {/* Page number */}
              <span className="absolute top-1 left-1 bg-black/50 text-white text-[10px] leading-none rounded px-1 py-0.5 z-10">
                {idx + 1}
              </span>
              {/* Preview */}
              {page.previewUrl ? (
                <img
                  src={page.previewUrl}
                  alt={`Page ${idx + 1}`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <FileImage className="h-8 w-8 text-muted-foreground" />
              )}
              {/* Remove */}
              <button
                type="button"
                className="absolute top-1 right-1 bg-black/50 hover:bg-black/75 text-white rounded-full w-4 h-4 flex items-center justify-center z-10"
                onClick={() => onRemove(page.id)}
                aria-label={`Remove page ${idx + 1}`}
              >
                <X className="h-2.5 w-2.5" />
              </button>
            </div>
          ))}

          {/* Add more pages */}
          {pages.length < maxPages && (
            <button
              type="button"
              onClick={onAdd}
              className="w-24 h-24 rounded-md border-2 border-dashed border-border flex items-center justify-center cursor-pointer hover:border-primary/50 transition-colors"
              aria-label="Add another page"
            >
              <Plus className="h-5 w-5 text-muted-foreground" />
            </button>
          )}
        </div>
      )}

      {error && (
        <p className="text-sm font-medium text-destructive">{error}</p>
      )}
    </div>
  );
}
